import { isThreadUnreadByReadAt } from "../../script-comment-thread-reads";
import type {
  CommentPanelFilters,
  CommentThreadRow,
  FilterCommentThreadsContext,
} from "./scriptCommentsPanelFilters.types";

/** Default drawer filters (matches prior “Unresolved” tab). */
export const DEFAULT_COMMENT_PANEL_FILTERS: CommentPanelFilters = {
  feature: "all",
  status: "open",
  read: "all",
};

/** Clears every filter so scroll-to-thread can reveal hidden threads. */
export const CLEAR_COMMENT_PANEL_FILTERS: CommentPanelFilters = {
  feature: "all",
  status: "all",
  read: "all",
};

export function getCommentThreadFeature(
  comment: CommentThreadRow["comment"]
): "screenplay" | "schedule" {
  const feature = comment.anchors?.[0]?.feature;
  return feature === "schedule" ? "schedule" : "screenplay";
}

/** The filters that apply: the read filter waits while read times are unknown. */
export function effectiveCommentPanelFilters(
  filters: CommentPanelFilters,
  readTimesKnown: boolean
): CommentPanelFilters {
  return readTimesKnown || filters.read === "all" ? filters : { ...filters, read: "all" };
}

export function isCommentPanelFilterActive(filters: CommentPanelFilters): boolean {
  return (
    filters.feature !== CLEAR_COMMENT_PANEL_FILTERS.feature ||
    filters.status !== CLEAR_COMMENT_PANEL_FILTERS.status ||
    filters.read !== CLEAR_COMMENT_PANEL_FILTERS.read
  );
}

export function filterCommentThreads(
  threads: CommentThreadRow[],
  filters: CommentPanelFilters,
  context: FilterCommentThreadsContext
): CommentThreadRow[] {
  return threads.filter(({ comment, replies }) => {
    if (context.replyTargetRootId === comment.comment_id) {
      return true;
    }

    if (filters.feature !== "all") {
      if (getCommentThreadFeature(comment) !== filters.feature) {
        return false;
      }
    }

    if (filters.status === "open" && comment.status !== "open") {
      return false;
    }
    if (filters.status === "resolved" && comment.status !== "resolved") {
      return false;
    }

    // Unknown read times cannot sort threads into read and unread, so the read filter waits.
    if (filters.read === "all" || context.readAtByRoot === null) {
      return true;
    }

    const readAt = context.readAtByRoot[comment.comment_id];
    const unread = isThreadUnreadByReadAt(comment, replies, readAt, context.currentUserId);

    if (filters.read === "unread" && !unread) {
      return false;
    }
    if (filters.read === "read" && unread) {
      return false;
    }

    return true;
  });
}

export function commentPanelEmptyStateMessage(
  filters: CommentPanelFilters,
  scopeLabel: string
): string {
  const parts: string[] = [];

  if (filters.feature === "screenplay") {
    parts.push("script");
  } else if (filters.feature === "schedule") {
    parts.push("schedule");
  }

  if (filters.status === "open") {
    parts.push("unresolved");
  } else if (filters.status === "resolved") {
    parts.push("resolved");
  }

  if (filters.read === "unread") {
    parts.push("unread");
  } else if (filters.read === "read") {
    parts.push("read");
  }

  if (parts.length === 0) {
    return `No comments in this ${scopeLabel}.`;
  }

  return `No ${parts.join(", ")} comments in this ${scopeLabel}.`;
}
